/**
 * Projects Section Component
 * Showcase of portfolio projects with images, descriptions, and links
 */
import { Container, Row, Col, Button } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import GitHubIcon from '@mui/icons-material/GitHub';
import LaunchIcon from '@mui/icons-material/Launch';

import abSliderImg from "../assets/projects/compare_slider.png";
import imgSlider from "../assets/projects/image_slider.png";
import freshBake from "../assets/projects/freshback_landing.png";
import novaLand from "../assets/projects/NOVA-Landing-Page-Template.png";
import tmFood from "../assets/projects/TMfood.png";
import flixGptMovie from "../assets/projects/Flixgpt.png";
import CoopCourse from "../assets/projects/learning_sample.png";
import sparring from "../assets/projects/Sparring_partners.png"
import novaNor from "../assets/projects/Novo-Nordisk.png"
import tvoIlc from "../assets/projects/tvoilc.png"
import tvoLearn from "../assets/projects/tvolearn.png"

const Projects = () => {
  // Intersection observer for scroll animations
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  // Projects data
  const projects = [
    {
      title: 'TVO Learn',
      description: 'Worked on a large-scale eLearning platform, built responsive, accessible UI with performance and analytics improvements.',
      image: tvoLearn,
      tags: ['HTML', 'CSS', 'JavaScript', 'React', 'webpack', 'Shopify', 'LMS', 'REST APIs'],
      liveUrl: 'https://tvolearn.com/',
    },
     {
      title: 'TVO ILC',
      description: 'Worked on a large-scale eLearning platform, built responsive, accessible UI with performance and analytics improvements.',
      image: tvoIlc,
      tags: ['HTML', 'CSS', 'JavaScript', 'React', 'webpack', 'Shopify', 'LMS', 'REST APIs'],
      liveUrl: 'https://www.ilc.org/',
    },
    {
      title: 'Food Ordering App',
      description: 'A full-featured online store with cart functionality.',
      image: tmFood,
      tags: ['React', 'Redux Toolkit', 'Tailwind CSS', 'REST API Integration', 'Jest', 'Parcel'],
      githubUrl: 'https://github.com/riddhipatel-design/project_react',
    },
    {
      title: 'FlixGPT Movie App',
      description: 'A movie discovery app that combines real-time data with suggestions.',
      image: flixGptMovie,
      tags: [ "React",
      "Redux Toolkit",
      "Tailwind CSS",
      "Firebase",
      "TMDb API",
      "OpenAI GPT"],
      liveUrl: 'https://flixgptmovies.netlify.app/',
      
    },
    {
      title: 'Nova Landing Page',
      description: 'A clean, modern landing experience designed to captivate users with smooth navigation and visually appealing layout.',
      image: novaLand,
      tags: ["React", "CSS", "Vite", "Styled Components"],
      liveUrl: "https://nova-page.netlify.app/",
    },
    {
      title: 'Coop eLearning Platform',
      description: 'An intuitive eLearning course interface designed for seamless user learning and engagement.',
      image: CoopCourse,
      tags: ["React", "React Router", "Tailwind CSS", "Shadcn UI", "Radix UI", "Lucide React"],
      liveUrl: 'https://coop-elearningcourse.netlify.app/',
     
    },
    {
      title: 'FreshBake Foods',
      description: 'A sleek, user-focused interface crafted for clear presentation and engaging visuals.',
      image: freshBake,
      tags: ["HTML", "CSS", "JavaScript", "Bootstrap"],
      liveUrl: 'https://freshback.netlify.app/',
    },
    {
      title: 'Sparring Partners Capital',
      description: 'WordPress-based corporate website with custom front-end styling and layout enhancements.',
      image: sparring,
      tags: ['WordPress', 'HTML5', 'CSS3', 'JavaScript', 'jQuery'],
      liveUrl: 'https://sparringpartners.com/',
    },
   
     {
      title: 'Novo Nordisk',
      description: 'An enterprise healthcare website built on Adobe Experience Manager, focused on structured content delivery, accessibility, and scalability.',
      image: novaNor,
      tags: ['Adobe Experience Manager', 'HTML5', 'CSS3', 'JavaScript'],
      liveUrl: 'https://www.novonordisk-us.com/',
    },
    
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 } 
    }
  };

  return (
    <section id="projects" className="projects-section" ref={ref} style={{paddingTop: "80px" }}>
      <Container>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-title" style={{ marginBottom: "32px" }}>Projects</h2>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Row className="g-4">
            {projects.map((project, index) => (
              <Col key={index} md={6} lg={4}>
                <motion.div variants={itemVariants}>
                  <div className="project-card">
                    {/* Project Image */}
                    <div className="project-image-wrapper">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="project-image"
                        loading="lazy"
                      />
                      <div className="project-overlay">
                       {project.liveUrl && ( <Button
                          variant="light"
                          size="sm"
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="me-2"
                        >
                          <LaunchIcon fontSize="small" />
                        </Button> )}
                        {project.githubUrl && (<Button
                          variant="light"
                          size="sm"
                          href={project.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <GitHubIcon fontSize="small" />
                        </Button>)}
                      </div>
                    </div>

                    {/* Project Content */}
                    <div className="project-content">
                      <h3 className="project-title">{project.title}</h3>
                      <p className="project-description">{project.description}</p>
                      
                      {/* Tags */}
                      <div className="project-tags">
                        {project.tags.map((tag, tagIndex) => (
                          <span key={tagIndex} className="project-tag">
                            {tag}
                          </span>
                        ))}
                      </div>

                      {/* Links */}
                      <div className="project-links">
                        {project.liveUrl && (<a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-link"
                        >
                          <LaunchIcon fontSize="small" /> Live Demo
                        </a> )}
                        {project.githubUrl && (<a
                          href={project.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-link"
                        >
                          <GitHubIcon fontSize="small" /> Source Code
                        </a> )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </motion.div>
      </Container>
    </section>
  );
};

export default Projects;
